import httpClient from "../http/http-client";
import { IEmission, IEmissionDetail, IEditEmission, IEmissionAuditTrail, IEmissionDocument, IEmissionFactorOption, ISpendTotal } from './emission.model';

export default class EmissionService {
  http;

  constructor() {
    this.http = httpClient
  }

  /**
   * `filteredTotalEmissionsKg` is the sum across the WHOLE filtered set, not just the page, gated
   * identically to each row's `totalEmissionsKg` (selected calculations, upstream/WTT excluded).
   * `filteredSpendTotals` is the entered spend of the same set, one figure per currency.
   */
  async getAll(reportPeriodId: string, page: number, perPage: number = 10, orderBy?: string, order?: 'asc' | 'desc', queryString?: string): Promise<{ count: number, filteredTotalEmissionsKg: number, filteredSpendTotals: ISpendTotal[], rows: IEmission[] }> {
    return this.http.get(`emission?reportPeriodId=${reportPeriodId}&page=${page}&perPage=${perPage}${order && orderBy ? `&orderBy=${orderBy}&order=${order}` : ''}${queryString ? queryString : ''}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async getByDomainHighest(
    reportPeriodId: string,
    group: string,
    page: number = 0,
    perPage: number = 10,
    orderBy?: string,
    order?: 'asc' | 'desc',
  ): Promise<{ count: number; rows: IEmission[] }> {
    const queryString = `&group=${encodeURIComponent(group)}`;
    return this.http
      .get(
        `emission/highest?reportPeriodId=${reportPeriodId}&page=${page}&perPage=${perPage}${order && orderBy ? `&orderBy=${orderBy}&order=${order}` : ''}${queryString}`,
      )
      .then((response) => response.data)
      .catch((err) => {
        console.log(err);
        throw err;
      });
  }

  /** Distinct emission factors used in the report period, for the Emission Factor list filter. */
  async getFactorOptions(reportPeriodId: string): Promise<{ rows: IEmissionFactorOption[] }> {
    return this.http.get(`emission/factor-options?reportPeriodId=${reportPeriodId}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async getOne(emissionId: string): Promise<IEmissionDetail> {
    return this.http.get(`emission/${emissionId}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async getAuditTrail(emissionId: string): Promise<IEmissionAuditTrail> {
    return this.http.get(`emission/${emissionId}/audit-trail`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async getDocuments(emissionId: string): Promise<IEmissionDocument[]> {
    return this.http.get(`emission/${emissionId}/documents`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  /** Uploads evidence files; resolves to the refreshed document list. */
  async uploadDocuments(emissionId: string, files: File[]): Promise<IEmissionDocument[]> {
    const form = new FormData()
    files.forEach(file => form.append('files', file))
    return this.http.post(`emission/${emissionId}/documents`, form)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  /** Soft-deletes the document row only; the stored file is retained. */
  async removeDocument(emissionId: string, resourceId: string): Promise<IEmissionDocument[]> {
    return this.http.delete(`emission/${emissionId}/documents/${resourceId}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async deleteEmissions(emissionIds: string[]): Promise<void> {
    return this.http.post(`emission/delete`, emissionIds)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async deleteAllEmissions(reportPeriodId: string, queryString?: string): Promise<void> {
    const normalizedQuery = queryString?.replace(/^[?&]/, '').replace(/\?/g, '&') ?? '';
    const queryParams = normalizedQuery ? Object.fromEntries(new URLSearchParams(normalizedQuery)) : {};

    return this.http.post(`emission/delete-all`, { reportPeriodId, ...queryParams })
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async editSelectedEmissions(
    ids: string[],
    emission: Partial<IEditEmission> & { customFieldValues?: { customFieldId: string; value: string | null }[] },
  ): Promise<void> {
    return this.http.patch(`emission/edit/selected`, { ids, emission })
      .then(response => response.data)
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async editAllEmissions(
    reportPeriodId: string,
    emission: Partial<IEditEmission> & { customFieldValues?: { customFieldId: string; value: string | null }[] },
    queryString?: string,
  ): Promise<void> {
    const normalizedQuery = queryString?.replace(/^[?&]/, '').replace(/\?/g, '&') ?? '';
    const queryParams = normalizedQuery ? Object.fromEntries(new URLSearchParams(normalizedQuery)) : {};
    
    return this.http.patch(`emission/edit/all`, { reportPeriodId, emission, ...queryParams })
      .then(response => response.data)
      .catch(err => {
        console.log(err);
        throw err;
      });
  }
}